import { Option } from '@beeline/design-system-react/extracted-styles';

import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Building, Floor, Town } from 'src/api/types';
import { PlaceType } from 'src/types';
import { findLeastTime } from 'src/utils/findLeastTime';
import { formatTime } from 'src/utils/formatTime';
import { getDateTime } from 'src/utils/getDateTime';
import { toTime } from 'src/utils/toTime';
import { validateDate } from 'src/utils/validations/validateDate';

import { getIsMobileWithTouchscreen } from '../agent/selectors';
import { fetchLastBookings } from '../lastBookings/services';
import { startAppListening } from '../listenerMiddleware';
import { sidesheetActions } from '../sidesheet/slice';

import { defaultRoomBookingFilters, initialState } from './initialState';
import { fetchRooms, fetchRoomStatuses } from './services';
import { calculateDateBounds, calculateRoomsTimeError } from './utils';

const defaultBookingTimeInMinutes = 30;
const defaultIntervalMs = 15 * 60 * 1000;

const getCurrentDateTime = (
  town: Town | undefined,
): { date: string | undefined; time: string } => {
  if (town?.timeZone === undefined) return { date: undefined, time: '' };

  const date = getDateTime({ timezoneOffset: town.timeZone });
  const initialDateFrom = findLeastTime(date, defaultIntervalMs);
  const initialDateTo = new Date(initialDateFrom);
  initialDateTo.setMinutes(
    initialDateTo.getMinutes() + defaultBookingTimeInMinutes,
  );
  const time = `${formatTime(initialDateFrom) ?? ''}—${formatTime(initialDateTo) ?? ''}`;
  return { date: date.toISOString(), time };
};

export const roomStatusesPollingStarted = createAction<
  boolean,
  'rooms/statusesPollingStarted'
>('rooms/statusesPollingStarted');
export const roomStatusesPollingStopped = createAction(
  'rooms/statusesPollingStopped',
);

export const roomsSlice = createSlice({
  name: 'rooms',
  initialState,
  reducers: {
    setTown: (state, action: PayloadAction<Town | undefined>) => {
      state.town = action.payload;

      const { date, time } = getCurrentDateTime(action.payload);
      state.date = date;
      state.time = time;
    },
    setBuilding: (state, action: PayloadAction<Building | undefined>) => {
      state.building = action.payload;
    },
    setFloor: (state, action: PayloadAction<Floor | undefined>) => {
      state.floor = action.payload;
    },
    clearLocation: state => {
      state.town = undefined;
      state.building = undefined;
      state.floor = undefined;
    },
    setDate: (state, action: PayloadAction<string | undefined>) => {
      state.date = action.payload;

      const timezoneOffset = state.town?.timeZone;
      if (timezoneOffset !== undefined) {
        const { min, max } = calculateDateBounds(timezoneOffset);
        const dateError = validateDate(action.payload, min, max);
        const [from, to] = state.time.split('—');
        const timeError = calculateRoomsTimeError({
          date: action.payload,
          from,
          to,
          timezoneOffset: state.town?.timeZone,
        });
        state.dateError = dateError;
        state.timeError = timeError;
      }
    },
    setTime: (state, action: PayloadAction<string>) => {
      state.time = action.payload;

      const [from, to] = action.payload.split('—');
      const timeError = calculateRoomsTimeError({
        date: state.date,
        from,
        to,
        timezoneOffset: state.town?.timeZone,
      });
      state.timeError = timeError;
    },
    validateDate: state => {
      if (state.town?.timeZone !== undefined) {
        const { min, max } = calculateDateBounds(state.town.timeZone);
        const dateError = validateDate(state.date, min, max);
        state.dateError = dateError;
      }
    },
    validateTime: state => {
      const [from, to] = state.time.split('—');
      const timeError = calculateRoomsTimeError({
        date: state.date,
        from,
        to,
        timezoneOffset: state.town?.timeZone,
      });
      state.timeError = timeError;
    },
    setDateError: (state, action: PayloadAction<string | undefined>) => {
      state.dateError = action.payload;
    },
    setTimeError: (state, action: PayloadAction<string>) => {
      state.timeError = action.payload;
    },
    setSeatCount: (state, action: PayloadAction<Option<number>[]>) => {
      state.seatCount = action.payload;
    },
    setWithTv: (state, action: PayloadAction<boolean>) => {
      state.withTv = action.payload;
    },
    setWithVideoCalls: (state, action: PayloadAction<boolean>) => {
      state.withVideoCalls = action.payload;
    },
    setWithFlipChart: (state, action: PayloadAction<boolean>) => {
      state.withFlipChart = action.payload;
    },
    setWithWater: (state, action: PayloadAction<boolean>) => {
      state.withWater = action.payload;
    },
    setSeatCountFilter: (state, action: PayloadAction<Option<number>[]>) => {
      state.filters = {
        ...state.filters,
        seatCount: action.payload,
      };
    },
    setWithTvFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withTv: action.payload,
      };
    },
    setWithVideoCallsFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withVideoCalls: action.payload,
      };
    },
    setWithFlipChartFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withFlipChart: action.payload,
      };
    },
    setWithWaterFilter: (state, action: PayloadAction<boolean>) => {
      state.filters = {
        ...state.filters,
        withWater: action.payload,
      };
    },
    applyFilters: state => {
      state.filters = {
        seatCount: state.seatCount,
        withTv: state.withTv,
        withVideoCalls: state.withVideoCalls,
        withFlipChart: state.withFlipChart,
        withWater: state.withWater,
      };
    },
    resetFilters: state => {
      state.filters = defaultRoomBookingFilters;
      state.withTv = defaultRoomBookingFilters.withTv;
      state.withVideoCalls = defaultRoomBookingFilters.withVideoCalls;
      state.withFlipChart = defaultRoomBookingFilters.withFlipChart;
      state.withWater = defaultRoomBookingFilters.withWater;
    },
    setRoomId: (state, action: PayloadAction<string | undefined>) => {
      state.roomId = action.payload;
    },
    setTimeRange: (
      state,
      action: PayloadAction<{ from: Date | undefined; to: Date | undefined }>,
    ) => {
      state.date = action.payload.from?.toISOString();
      state.time = `${toTime(action.payload.from) ?? ''}—${toTime(action.payload.to) ?? ''}`;
    },
    setRoomBookingSheetIsOpened: (state, action: PayloadAction<boolean>) => {
      state.roomBookingSheetIsOpened = action.payload;
    },
    openRoomBookingSheet: state => {
      state.roomBookingSheetIsOpened = true;
    },
    closeRoomBookingSheet: state => {
      state.roomBookingSheetIsOpened = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchRooms.pending, (state, action) => {
      state.error = undefined;
      if (action.meta.arg) state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchRooms.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchRooms.fulfilled, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.bookingObjects = action.payload.bookingObjects;

      if (action.meta.arg) {
        state.backgroundData = action.payload.backgroundData;
        state.staticObjects = action.payload.staticObjects;
      }

      state.currentRequestId = undefined;
    });

    builder.addCase(fetchLastBookings.pending, state => {
      state.locationError = undefined;
      state.locationLoading = true;
    });
    builder.addCase(fetchLastBookings.rejected, (state, action) => {
      state.locationError = action.payload;
      state.locationLoading = false;
    });
    builder.addCase(fetchLastBookings.fulfilled, (state, action) => {
      state.locationLoading = false;

      if (state.town && state.building && state.floor) return;

      const roomLocation = action.payload[PlaceType.Room];
      const town = roomLocation?.town;
      const building = roomLocation?.building;
      const floor = roomLocation?.floor.isParking
        ? undefined
        : roomLocation?.floor;

      state.town = town;
      state.building = building;
      state.floor = floor;

      const { date, time } = getCurrentDateTime(town);
      state.date = date;
      state.time = time;
    });

    builder.addCase(fetchRoomStatuses.fulfilled, (state, action) => {
      state.slots = { ...action.payload, requestId: action.meta.requestId };
    });

    builder.addCase(fetchRoomStatuses.pending, state => {
      state.slotsError = undefined;
      state.slotsLoading = true;
    });
    builder.addCase(fetchRoomStatuses.rejected, (state, action) => {
      state.slotsError = action.payload;
      state.slotsLoading = false;
    });
  },
});

export const { actions: roomsActions, reducer: roomsReducer } = roomsSlice;

export const roomsPollingStarted = createAction<boolean, 'rooms/started'>(
  'rooms/started',
);
export const roomsPollingStopped = createAction('rooms/stopped');
export const openRoom = createAction<string, 'rooms/open'>('rooms/open');

startAppListening({
  actionCreator: roomsActions.setDate,
  effect: async (action, listenerApi) => {
    await listenerApi.dispatch(roomsPollingStopped());
    await listenerApi.dispatch(fetchRooms(false));
    await listenerApi.dispatch(roomsPollingStarted(false));
  },
});

startAppListening({
  actionCreator: roomsActions.setTime,
  effect: async (action, listenerApi) => {
    await listenerApi.dispatch(roomsPollingStopped());
    await listenerApi.dispatch(fetchRooms(false));
    await listenerApi.dispatch(roomsPollingStarted(false));
  },
});

startAppListening({
  actionCreator: roomsPollingStarted,
  effect: async (action, listenerApi) => {
    listenerApi.unsubscribe();

    if (action.payload) await listenerApi.dispatch(fetchRooms(false));

    const refreshTask = listenerApi.fork(async forkApi => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await forkApi.delay(10000);
        // eslint-disable-next-line no-await-in-loop
        await listenerApi.dispatch(fetchRooms(false));
      }
    });

    await listenerApi.condition(roomsPollingStopped.match);
    refreshTask.cancel();

    listenerApi.subscribe();
  },
});

startAppListening({
  actionCreator: openRoom,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();

    const isMobile = getIsMobileWithTouchscreen(state);

    listenerApi.dispatch(roomsActions.setRoomId(action.payload));
    if (isMobile) {
      listenerApi.dispatch(roomsActions.openRoomBookingSheet());
    } else {
      listenerApi.dispatch(sidesheetActions.open('roomBooking'));
    }
  },
});

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.sidesheet.current !== 'roomBooking' &&
      previous.sidesheet.current === 'roomBooking'
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(roomsActions.setRoomId(undefined));
  },
});

startAppListening({
  actionCreator: roomStatusesPollingStarted,
  effect: async (action, listenerApi) => {
    listenerApi.unsubscribe();

    if (action.payload) await listenerApi.dispatch(fetchRoomStatuses());

    const refreshTask = listenerApi.fork(async forkApi => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await forkApi.delay(10000);
        // eslint-disable-next-line no-await-in-loop
        await listenerApi.dispatch(fetchRoomStatuses());
      }
    });

    await listenerApi.condition(roomStatusesPollingStopped.match);
    refreshTask.cancel();

    listenerApi.subscribe();
  },
});
